import { useState } from 'react';

import { Button } from '@/components/ui/button';

import type { FreshStrawSession } from '../../hooks/useFreshStrawSession';
import { useFreshStrawDairyCompReport } from '../../reports';
import { FreshStrawFreshEmbryoImportDialog } from '../FreshStrawFreshEmbryoImportDialog';
import { FreshStrawPregCheckImportDialog } from '../FreshStrawPregCheckImportDialog';
import { FreshStrawStageEmbryosDialog } from '../FreshStrawStageEmbryosDialog';
import { FreshStrawTransferImportDialog } from '../FreshStrawTransferImportDialog';

interface FreshStrawUploadTabProps {
  session: FreshStrawSession;
}

interface UploadCardProps {
  title: string;
  description: string;
  actionLabel: string;
  onClick: () => void;
  disabled?: boolean;
  disabledReason?: string;
}

function UploadCard({
  title,
  description,
  actionLabel,
  onClick,
  disabled,
  disabledReason,
}: UploadCardProps) {
  return (
    <div className="ring-border bg-background flex flex-col gap-3 rounded-lg p-4 ring-1">
      <div className="flex flex-col gap-1">
        <div className="text-sm font-semibold">{title}</div>
        <div className="text-muted-foreground text-xs">{description}</div>
      </div>
      <div className="flex items-center gap-3">
        <Button size="sm" onClick={onClick} disabled={disabled}>
          {actionLabel}
        </Button>
        {disabled && disabledReason && (
          <span className="text-muted-foreground text-xs">
            {disabledReason}
          </span>
        )}
      </div>
    </div>
  );
}

export function FreshStrawUploadTab({ session }: FreshStrawUploadTabProps) {
  const [transferOpen, setTransferOpen] = useState(false);
  const [pregCheckOpen, setPregCheckOpen] = useState(false);
  const [freshEmbryoOpen, setFreshEmbryoOpen] = useState(false);
  const [stageOpen, setStageOpen] = useState(false);
  const dairyComp = useFreshStrawDairyCompReport();

  const dairyCompReady =
    !!session.transferDate && !!session.transferSite && !!session.fertSite;

  const runDairyComp = () => {
    if (!dairyCompReady) return;
    dairyComp.mutate({
      parameters: {
        transferDate: session.transferDate,
        transferSiteId: session.transferSite,
        fertilizationSiteId: session.fertSite,
        accountId: session.accountId || undefined,
        freshShipmentId: session.freshShipmentId || undefined,
      },
    });
  };

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-auto p-1">
      <div className="text-muted-foreground text-sm">
        Upload partner spreadsheets that populate transfers, preg-check results,
        or new fresh embryos. Each importer parses its own header variants
        (normal and Dairy Comp / FARIA) and reports per-row errors without
        aborting the batch.
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <UploadCard
          title="Import transfers"
          description="Upload an Excel sheet of transfers (normal or Dairy Comp header variant). Required columns: Recip + EMBID or Straw. Other columns are optional and override the filter-panel defaults for that row."
          actionLabel="Choose file…"
          onClick={() => setTransferOpen(true)}
        />

        <UploadCard
          title="Import preg-check results"
          description="Upload a preg-check workbook. Regular columns: DairyCompID, PCDate, PCResult, SiteID. FARIA DCC files auto-score every row as Preg — toggle the FARIA checkbox inside the dialog."
          actionLabel="Choose file…"
          onClick={() => setPregCheckOpen(true)}
        />

        <UploadCard
          title="Export DairyComp CSV"
          description="Run the legacy DairyComp transfer CSV export. Uses the TOA_GetFreshStrawTransferCSV stored proc and writes the 17-column DairyComp-compatible layout. Requires transfer date, transfer site, and fertilization site to be set in the filter panel."
          actionLabel={dairyComp.isPending ? 'Generating…' : 'Generate CSV'}
          onClick={runDairyComp}
          disabled={!dairyCompReady || dairyComp.isPending}
          disabledReason={
            !dairyCompReady
              ? 'Set transfer date, transfer site, and fertilization site first'
              : undefined
          }
        />

        <UploadCard
          title="Stage fresh embryos for import"
          description="Seed blank staging rows for a fertilization. Required first step before importing a filled-in fresh-embryo template; the template matches on the embryoid assigned here."
          actionLabel="Stage embryos"
          onClick={() => setStageOpen(true)}
        />

        <UploadCard
          title="Import fresh embryos"
          description="Upload the completed fresh-embryo template (Embryos sheet). Each row is matched on embryoid to a staged record; straw #, stage, grade, recip, froze, and discard are applied and embryos are promoted into the main table."
          actionLabel="Choose file…"
          onClick={() => setFreshEmbryoOpen(true)}
        />
      </div>

      <FreshStrawTransferImportDialog
        open={transferOpen}
        onOpenChange={setTransferOpen}
        defaultSiteId={session.transferSite || undefined}
        defaultFarmLocationId={session.locationId || undefined}
        defaultTransferDate={session.transferDate || undefined}
      />

      <FreshStrawPregCheckImportDialog
        open={pregCheckOpen}
        onOpenChange={setPregCheckOpen}
      />

      <FreshStrawFreshEmbryoImportDialog
        open={freshEmbryoOpen}
        onOpenChange={setFreshEmbryoOpen}
      />

      <FreshStrawStageEmbryosDialog
        open={stageOpen}
        onOpenChange={setStageOpen}
        session={session}
      />
    </div>
  );
}
